// Copyright (c) 2026 Michael F. Kamprath
//
// learn-mode.js -- Async state machine that drives Learn-mode rounds.
//
// This module doesn't touch PixiJS or settings directly.  It orchestrates
// the CONVERSION-PLAN section 4 flow:
//
//   SCENE_SETUP -> ENTRANCE_ANIMATION -> COUNTING_PROMPT -> COUNTING
//     -> LEARN_SUCCESS -> (pause + interjection) -> SCENE_SETUP ...
//
// The caller supplies a `startRound` async callback (typically
// implemented in app.js) that:
//   1. Tears down the previous scene if one exists.
//   2. Builds the new scene for the given theme + object count.
//   3. Wires up the entrance animation, idle manager, learn-tap manager,
//      and count model.
//   4. Returns a `Round` handle with three Promises (entrance done,
//      completion, explicit teardown) and a reference to the countModel.
//
// By keeping PixiJS out of this module, we can unit-test the full state
// machine with injected fakes and zero rendering.
//
// Cancellation is via a PlaybackToken: calling stop() marks the token
// cancelled, which each await boundary in the run loop checks, tears
// down the in-flight round, and exits the loop.

/**
 * @typedef {Object} Round
 * @property {import('./count-model.js').CountModel} countModel
 * @property {Promise<void>} whenEntranceDone
 *   Resolves when the entrance animation has finished and the idle +
 *   tap managers are armed.  Before this resolves, COUNTING_PROMPT
 *   state is not yet entered.
 * @property {Promise<void>} whenComplete
 *   Resolves when the countModel's onComplete fires (all objects
 *   counted).  The state machine awaits this to leave the COUNTING
 *   state.
 * @property {() => Promise<void>} teardown
 *   Destroys the scene, managers, and cancels any in-flight plaque
 *   animations.  Safe to call multiple times.
 */

/**
 * @typedef {Object} LearnModeDeps
 * @property {import('./theme-pool.js')} themePool
 *   Round-rotation theme source (must implement draw()).
 * @property {() => number} getMaxCount
 *   Max object count setting; used with pickObjectCount to choose the
 *   per-round sprite count.
 * @property {() => Object|null} pickVoiceSet
 *   Returns the voice set to use for the next round.  Typically wraps
 *   pickVoiceSetForLocale.
 * @property {Object} gameAudio
 *   Object returned by createGameAudio.  Must expose
 *   playNewThemeIntro, playLearnIntro, playLearnSuccess,
 *   playInterjection.  (The session-start prompt
 *   "Let's learn to count!" is played by the home screen before the
 *   machine starts, so this module no longer needs it.)
 * @property {(ctx: { themeId: string, objectCount: number, voiceSet: Object, token: Object }) => Promise<Round>} startRound
 *   Caller-provided round builder; see Round typedef.
 * @property {() => { cancelled: boolean, cancel: () => void }} createToken
 *   PlaybackToken factory.
 * @property {(seconds: number) => Promise<void>} pause
 *   Sleep helper.
 * @property {(maxCount: number) => number} [pickObjectCount]
 *   How to choose an object count given the max-count setting.  Default:
 *   uniform random in [1, maxCount].  Injectable for tests.
 * @property {number} [interRoundDelay]
 *   Seconds of silence between LEARN_SUCCESS praise and the inter-round
 *   interjection.  Default 0.8.
 * @property {number} [postCountDelay]
 *   Seconds of silence between the final tap's number audio finishing
 *   (round.whenComplete) and the "Good counting!" praise in
 *   LEARN_SUCCESS.  Gives the user a beat to hear the last number before
 *   the praise lands.  Default 0.6.
 * @property {(state: string, meta?: object) => void} [onState]
 *   Observer for state transitions (used by DEV logging + later telemetry).
 * @property {() => number} [rng]
 *   RNG for object-count picking.  Defaults to Math.random.
 */

export const STATES = Object.freeze({
  IDLE: 'IDLE',
  SCENE_SETUP: 'SCENE_SETUP',
  ENTRANCE_ANIMATION: 'ENTRANCE_ANIMATION',
  COUNTING_PROMPT: 'COUNTING_PROMPT',
  COUNTING: 'COUNTING',
  LEARN_SUCCESS: 'LEARN_SUCCESS',
  INTERLUDE: 'INTERLUDE',
  STOPPED: 'STOPPED',
});

const noop = () => {};

function defaultPickObjectCount(maxCount, rng) {
  const clamped = Math.max(1, Math.min(20, Math.floor(maxCount || 1)));
  return Math.floor(rng() * clamped) + 1;
}

/**
 * Create a learn-mode state machine.  The machine is dormant until
 * start() is called.  stop() cancels it mid-round (tears down the
 * in-flight scene via round.teardown() and exits the loop).
 *
 * @param {LearnModeDeps} deps
 */
export function createLearnModeMachine(deps) {
  const {
    themePool,
    getMaxCount,
    pickVoiceSet,
    gameAudio,
    startRound,
    createToken,
    pause,
    pickObjectCount,
    interRoundDelay = 0.8,
    postCountDelay = 0.6,
    onState = noop,
    rng = Math.random,
    // Telemetry: optional.  Fired at round-end with either
    // completed=true (LEARN_SUCCESS) or completed=false (cancelled
    // mid-round via stop()).  Defaults to a no-op so the machine
    // runs fine in tests that don't wire telemetry.
    trackRoundPlayed = noop,
  } = deps;

  if (!themePool || typeof themePool.draw !== 'function') {
    throw new TypeError('createLearnModeMachine: themePool with draw() required');
  }
  if (typeof getMaxCount !== 'function') {
    throw new TypeError('createLearnModeMachine: getMaxCount function required');
  }
  if (typeof pickVoiceSet !== 'function') {
    throw new TypeError('createLearnModeMachine: pickVoiceSet function required');
  }
  if (!gameAudio) {
    throw new TypeError('createLearnModeMachine: gameAudio required');
  }
  if (typeof startRound !== 'function') {
    throw new TypeError('createLearnModeMachine: startRound function required');
  }
  if (typeof createToken !== 'function') {
    throw new TypeError('createLearnModeMachine: createToken function required');
  }
  if (typeof pause !== 'function') {
    throw new TypeError('createLearnModeMachine: pause function required');
  }

  const pickCount = typeof pickObjectCount === 'function'
    ? pickObjectCount
    : (mc) => defaultPickObjectCount(mc, rng);

  let running = false;
  let sessionToken = null;
  let lastState = STATES.IDLE;
  /** @type {Round | null} */
  let currentRound = null;
  /** @type {Promise<void> | null} */
  let runPromise = null;
  // Cancellation signal: stop() fulfills cancelPromise so any round-level
  // await boundary (entrance / whenComplete / inter-round pauses) can
  // unblock immediately instead of waiting for a user-driven promise that
  // will never resolve after cancellation.  The subsequent
  // `if (token.cancelled)` check then drives teardown + loop exit.
  let cancelResolve = null;
  /** @type {Promise<void> | null} */
  let cancelPromise = null;

  function armCancel() {
    cancelPromise = new Promise((r) => { cancelResolve = r; });
  }

  function cancellable(p) {
    return cancelPromise ? Promise.race([p, cancelPromise]) : p;
  }

  function setState(state, meta) {
    lastState = state;
    try { onState(state, meta); }
    catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.error('[learn-mode] onState threw:', err);
      }
    }
  }

  /**
   * In-flight round metadata for the `round_played` telemetry event.
   * Set when a round is built (post-startRound); cleared at the
   * happy-path emission point when LEARN_SUCCESS is reached.  If it
   * survives a mid-round teardown, the post-loop guard fires a
   * `completed: false` event.
   *
   * @type {{ themeId: string, objectCount: number, capturedTaps: number } | null}
   */
  let roundInFlight = null;

  function emitRoundCompleted() {
    if (!roundInFlight) return;
    // countModel exposes getCount() -- not a `.count` property.
    const liveCount = currentRound?.countModel?.getCount?.();
    const taps = Number.isFinite(liveCount) ? liveCount : roundInFlight.objectCount;
    try {
      trackRoundPlayed({
        mode: 'learn',
        theme: roundInFlight.themeId,
        count_value: roundInFlight.objectCount,
        completed: true,
        side_game_engaged: !!currentRound?.sideGameEngaged?.(),
        object_taps: taps,
        bee_taps: Number.isFinite(currentRound?.beeTaps?.()) ? currentRound.beeTaps() : 0,
        // Learn mode omits the quiz-only outcomes per spec §4.4.
        first_try: null,
        wrong_selections: 0,
      });
    } catch { /* non-fatal */ }
    roundInFlight = null;
  }

  function emitRoundAbandonedIfAny() {
    if (!roundInFlight) return;
    // capturedTaps was snapshotted by teardownCurrent() before the
    // countModel was destroyed; if teardown hasn't run yet (rare),
    // read live via getCount().
    const liveCount = currentRound?.countModel?.getCount?.();
    const taps = Number.isFinite(roundInFlight.capturedTaps)
      ? roundInFlight.capturedTaps
      : (Number.isFinite(liveCount) ? liveCount : 0);
    const sideGame = Number.isFinite(roundInFlight.sideGameEngaged)
      || typeof roundInFlight.sideGameEngaged === 'boolean'
      ? !!roundInFlight.sideGameEngaged
      : !!currentRound?.sideGameEngaged?.();
    const beeTaps = Number.isFinite(roundInFlight.beeTaps)
      ? roundInFlight.beeTaps
      : (Number.isFinite(currentRound?.beeTaps?.()) ? currentRound.beeTaps() : 0);
    try {
      trackRoundPlayed({
        mode: 'learn',
        theme: roundInFlight.themeId,
        count_value: roundInFlight.objectCount,
        completed: false,
        side_game_engaged: sideGame,
        object_taps: taps,
        bee_taps: beeTaps,
        first_try: null,
        wrong_selections: 0,
      });
    } catch { /* non-fatal */ }
    roundInFlight = null;
  }

  async function teardownCurrent() {
    // Snapshot object_taps into roundInFlight BEFORE destroying the
    // countModel, so the post-loop abandonment emit can report the
    // partial count even though teardown has already run.
    if (roundInFlight && typeof currentRound?.countModel?.getCount === 'function') {
      roundInFlight.capturedTaps = currentRound.countModel.getCount();
    }
    // Same for the side-game flag: snapshot before the round handle
    // is released so an abandonment emit still reports the correct
    // `side_game_engaged`.
    if (roundInFlight && typeof currentRound?.sideGameEngaged === 'function') {
      roundInFlight.sideGameEngaged = !!currentRound.sideGameEngaged();
    }
    // Same pattern for bee_taps (Flowers-only in practice; always 0
    // elsewhere).
    if (roundInFlight && typeof currentRound?.beeTaps === 'function') {
      const n = currentRound.beeTaps();
      roundInFlight.beeTaps = Number.isFinite(n) ? n : 0;
    }
    if (!currentRound) return;
    const round = currentRound;
    currentRound = null;
    try {
      await round.teardown();
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[learn-mode] round teardown threw:', err);
      }
    }
  }

  async function runSession() {
    const token = sessionToken;
    setState(STATES.SCENE_SETUP, { sessionStart: true });
    // The session-start prompt ("Let's learn to count!") is played by
    // the home screen before this machine starts, so we don't play it
    // again here -- see app.js playHomeSelectPrompt.

    // Track whether the next round is the very first round of the
    // session.  The learn-intro prompt ("Can you count the...?" /
    // "Touch each...") is a gameplay instruction; we only want to play
    // it on the first round so the player isn't repeatedly told how to
    // play.  Other audio (theme intro, success praise, interjections)
    // still runs every round.
    let firstRound = true;

    while (!token.cancelled) {
      const themeId = themePool.draw();
      if (!themeId) {
        // No active themes: the session has nothing to do.  Bail out.
        if (import.meta.env?.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[learn-mode] themePool.draw() returned null; exiting session');
        }
        break;
      }
      const maxCount = getMaxCount();
      const objectCount = pickCount(maxCount);
      const voiceSet = pickVoiceSet();

      setState(STATES.SCENE_SETUP, { themeId, objectCount });

      currentRound = await startRound({ themeId, objectCount, voiceSet, token });
      if (!currentRound) {
        // startRound refused (no scene could be built for this theme).
        // Skip to the next round.
        continue;
      }
      // Mark the round as in-flight.  If we break out before
      // LEARN_SUCCESS, the post-loop guard fires `completed: false`.
      roundInFlight = { themeId, objectCount, capturedTaps: null };
      if (token.cancelled) { await teardownCurrent(); break; }

      setState(STATES.ENTRANCE_ANIMATION, { themeId, objectCount });
      await cancellable(currentRound.whenEntranceDone);
      if (token.cancelled) { await teardownCurrent(); break; }

      setState(STATES.COUNTING_PROMPT, { themeId, objectCount });
      // "Let's count the sheep!"
      await gameAudio.playNewThemeIntro(themeId, voiceSet, token);
      if (token.cancelled) { await teardownCurrent(); break; }
      // "Can you count the sheep?" / "How many sheep?" / "Touch each sheep"
      // -- plays only on the first round of the session.  Subsequent
      // rounds skip this so the player isn't re-taught every round.
      if (firstRound) {
        await gameAudio.playLearnIntro(themeId, voiceSet, token);
        if (token.cancelled) { await teardownCurrent(); break; }
      }

      setState(STATES.COUNTING, { themeId, objectCount });
      await cancellable(currentRound.whenComplete);
      if (token.cancelled) { await teardownCurrent(); break; }

      // Breather between the final tap's number voice and the praise,
      // so "Good counting!" doesn't land right on top of "...three!".
      if (postCountDelay > 0) {
        await cancellable(pause(postCountDelay));
        if (token.cancelled) { await teardownCurrent(); break; }
      }

      setState(STATES.LEARN_SUCCESS, { themeId, objectCount });
      // Telemetry: the round has succeeded as soon as LEARN_SUCCESS is
      // entered -- the child counted every object.  Emit before the
      // success-praise playback so a visibilitychange-hidden during
      // the praise doesn't re-classify a completed round as abandoned.
      emitRoundCompleted();
      await gameAudio.playLearnSuccess(voiceSet, token);
      if (token.cancelled) { await teardownCurrent(); break; }

      setState(STATES.INTERLUDE, { themeId, objectCount });
      await cancellable(pause(interRoundDelay));
      if (token.cancelled) { await teardownCurrent(); break; }
      await gameAudio.playInterjection(voiceSet, token);

      // Tear down the finished round before we build the next one.  The
      // tap manager is destroyed here; a new countModel + learn-tap
      // manager get built by startRound() on the next iteration.
      await teardownCurrent();

      // Any subsequent round is no longer the "first round" -- the
      // learn-intro gameplay-instruction prompt has already played.
      firstRound = false;
    }

    // Session ended (cancelled or themePool exhausted).  If a round
    // was in flight when the loop broke, emit `completed: false`.
    // teardownCurrent() above already snapshotted capturedTaps before
    // destroying the countModel.
    emitRoundAbandonedIfAny();
    await teardownCurrent();
    setState(STATES.STOPPED, { cancelled: token.cancelled });
  }

  return {
    get state() {
      // For DEV inspection.  The authoritative state is emitted via
      // onState; this getter is a convenience for ad-hoc checks.
      return lastState;
    },

    /**
     * Begin the session.  Idempotent: calling twice returns the same
     * promise until stop() has resolved the first run.
     *
     * @returns {Promise<void>}  Resolves when the session exits.
     */
    start() {
      if (running) return runPromise;
      running = true;
      sessionToken = createToken();
      armCancel();
      lastState = STATES.IDLE;
      runPromise = runSession().finally(() => {
        running = false;
        cancelPromise = null;
        cancelResolve = null;
      });
      return runPromise;
    },

    /**
     * Cancel the session mid-round.  Awaits the in-flight round's
     * teardown, then resolves.  Safe to call if not running.
     *
     * @returns {Promise<void>}
     */
    async stop() {
      if (!running) return;
      if (sessionToken) sessionToken.cancel();
      // Unblock any round-level await (entrance, whenComplete, pauses)
      // so the run loop can immediately observe the cancelled token
      // instead of hanging on a promise that will never resolve after
      // the user bails mid-round.
      if (cancelResolve) cancelResolve();
      try { await runPromise; }
      catch { /* already logged inside runSession */ }
    },

    /**
     * True while a session is active (between start() and the run
     * promise resolving).
     */
    isRunning() { return running; },
  };
}
